import { api } from "./client";
import type {
  PlanTier,
  ListResult,
  OrganizationDetail,
  OrganizationRow,
  SystemInvitation,
  SystemOverview,
  UsageRow,
  UsageTotals,
  UserRow,
} from "@/types";

export type ListParams = { query?: string; page?: number; limit?: number };

const listParams = (params: ListParams) => ({
  params: {
    ...(params.query ? { query: params.query } : {}),
    page: params.page ?? 0,
    limit: params.limit ?? 25,
  },
});

export const login = (email: string, password: string) =>
  api.post<{ accessToken: string; email: string }>("/system/login", { email, password })
    .then((r) => r.data);

/**
 * Asks the server to mail a one-time code to the operator address.
 *
 * Sends no address: the server knows the only one it may use, so there is
 * nothing here to enumerate and no inbox to flood but the operator's own. It
 * does not answer with the address either.
 */
export const requestLoginCode = () =>
  api.post<{ expiresAt: string }>("/system/login/code").then((r) => r.data);

/** Spends a mailed code for the same operator token a password would give. */
export const verifyLoginCode = (code: string) =>
  api.post<{ accessToken: string; email: string }>("/system/login/verify", { code })
    .then((r) => r.data);

export const getOverview = () =>
  api.get<SystemOverview>("/system/overview").then((r) => r.data);

export const getOrganizations = (params: ListParams) =>
  api.get<ListResult<OrganizationRow>>("/system/organizations", listParams(params))
    .then((r) => r.data);

export const getOrganization = (id: string) =>
  api.get<OrganizationDetail>(`/system/organizations/${id}`).then((r) => r.data);

export const getUsers = (params: ListParams) =>
  api.get<ListResult<UserRow>>("/system/users", listParams(params)).then((r) => r.data);

export const setUserDisabled = (id: string, disabled: boolean) =>
  api.patch<{ id: string; disabled: boolean }>(`/system/users/${id}/disabled`, { disabled })
    .then((r) => r.data);

/**
 * Hands a plan to an account, or takes it back.
 *
 * Only for accounts with no store subscription — the server refuses the rest
 * with `STORE_SUBSCRIPTION`, because overwriting a real receipt would break
 * the link between the account and the store's notifications for good.
 */
export const grantPlan = (
  id: string,
  body: { tier: PlanTier; state?: "premium" | "grace" | "readonly"; expiresAt?: string },
) => api.patch<void>(`/system/users/${id}/subscription`, body).then(() => undefined);

export const revokePlan = (id: string) =>
  api.delete<void>(`/system/users/${id}/subscription`).then(() => undefined);

export const getInvitations = () =>
  api.get<{ invitations: SystemInvitation[] }>("/system/invitations")
    .then((r) => r.data.invitations);

export const getUsage = () =>
  api.get<{ items: UsageRow[]; totals: UsageTotals }>("/system/usage").then((r) => r.data);
